// frontend/src/pages/UsersPage.tsx - UPDATED WITH ORGANIZATION SELECTION
// Following the same pattern as ProjectsPage, WorkflowsPage for implementor organization selection

import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn, formatDate, truncateText } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';

// FIXED: Use entity-specific hooks following the pattern
import {
  useUsers,
  useUser,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  useActivateUser,
  useDeactivateUser,
  useResetUserPassword
} from '@/hooks/useUsers';

import { useOrganizations } from '@/hooks/useOrganizations';

// FIXED: Import API types
import type {
  User,
  Organization,
  NormalizedUser,
  UsersQueryParams,
  UserCreateRequest,
  UserUpdateRequest,
  PasswordResetRequest,
  UserRole
} from '@/api/types';

// ===== UTILITY FUNCTIONS =====

// Debounced value hook
function useDebounced<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// Simple organization interface for modal
interface SimpleOrganization {
  _id: string;
  name: string;
}

// Form data interfaces
interface UserFormData {
  username: string;
  email: string;
  phoneNumber?: string;
  role: UserRole;
  department: string;
  organizationId: string;
  generatePassword: boolean;
  password?: string;
  confirmPassword?: string;
}

interface PasswordResetData {
  newPassword: string;
  confirmPassword: string;
  generatePassword: boolean;
}

// ===== EDIT USER MODAL =====
const EditUserModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  user?: User;
  organizations: SimpleOrganization[];
  currentUser: any;
  onSuccess?: () => void;
}> = ({ isOpen, onClose, user, organizations, currentUser, onSuccess }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phoneNumber: '',
    role: '' as UserRole,
    department: '',
    organizationId: '',
    deactivated: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { setGlobalError, clearGlobalError } = useAppStore();

  const updateUserMutation = useUpdateUser({
    onSuccess: () => {
      onClose();
      clearGlobalError();
      onSuccess?.();
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to update user');
    }
  });

  // Initialize form data when user changes
  useEffect(() => {
    if (isOpen && user) {
      const userOrgId = typeof user.organization === 'string' 
        ? user.organization 
        : (user.organization as any)?._id || '';
        
      setFormData({
        username: user.username || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        role: user.role || 'contributor',
        department: user.department || '',
        organizationId: userOrgId,
        deactivated: user.deactivated || false
      });
      setErrors({});
    }
  }, [isOpen, user]);

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (formData.username.length > 30) {
      newErrors.username = 'Username must be less than 30 characters';
    } else if (!/^[a-zA-Z0-9._-]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, periods, underscores, and hyphens';
    }
    
    // Email is optional, but validate format if provided
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone number is optional, but validate format if provided
    if (formData.phoneNumber && formData.phoneNumber.trim()) {
      const digitsOnly = formData.phoneNumber.replace(/\D/g, '');
      if (!/^[\d\s\-\+\(\)]+$/.test(formData.phoneNumber)) {
        newErrors.phoneNumber = 'Phone number can only contain digits, spaces, and +()-';
      } else if (digitsOnly.length < 10) {
        newErrors.phoneNumber = 'Phone number must be at least 10 digits';
      }
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }
    
    // Role validation based on current user
    if (currentUser?.role === 'admin' && formData.role === 'implementor') {
      newErrors.role = 'Admins cannot set implementor role';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, currentUser?.role]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?._id && !user?.id) return;
    
    if (validateForm()) {
      const updateData: any = {
        username: formData.username.trim(),
        email: formData.email.trim() || undefined,
        phoneNumber: formData.phoneNumber?.trim() || undefined,
        role: formData.role,
        deactivated: formData.deactivated
      };

      if (formData.department?.trim()) {
        updateData.department = formData.department.trim();
      }
      
      updateUserMutation.mutate({
        id: user._id || user.id!,
        data: updateData as UserUpdateRequest
      });
    }
  }, [formData, validateForm, updateUserMutation, user]);

  const handleInputChange = useCallback((field: string) => 
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
      setFormData(prev => ({ ...prev, [field]: value }));
      
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }, [errors]);

  if (!isOpen || !user) return null;

  const canEditRole = currentUser?.role === 'implementor' || 
    (currentUser?.role === 'admin' && user.role !== 'implementor');

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Edit User: {user.username}
          </h3>

          <div className="space-y-4">
            {/* Username */}
            <div>
              <label htmlFor="edit-username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Username *
              </label>
              <Input
                id="edit-username"
                type="text"
                value={formData.username}
                onChange={handleInputChange('username')}
                placeholder="Enter username..."
                className={cn(errors.username && 'border-red-500 focus:border-red-500')}
                disabled={updateUserMutation.loading}
              />
              {errors.username && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.username}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email (optional)
              </label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                placeholder="Enter email address..."
                className={cn(errors.email && 'border-red-500 focus:border-red-500')}
                disabled={updateUserMutation.loading}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="edit-phoneNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phone Number (optional)
              </label>
              <Input
                id="edit-phoneNumber"
                type="tel"
                value={formData.phoneNumber || ''}
                onChange={handleInputChange('phoneNumber')}
                placeholder="Enter phone number..."
                className={cn(errors.phoneNumber && 'border-red-500 focus:border-red-500')}
                disabled={updateUserMutation.loading}
              />
              {errors.phoneNumber && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.phoneNumber}</p>
              )}
            </div>

            {/* Role */}
            <div>
              <label htmlFor="edit-role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role *
              </label>
              <select
                id="edit-role"
                value={formData.role}
                onChange={handleInputChange('role')}
                className={cn(
                  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.role && 'border-red-500 focus:border-red-500'
                )}
                disabled={updateUserMutation.loading || !canEditRole}
              >
                <option value="contributor">Contributor</option>
                <option value="admin">Admin</option>
                {currentUser?.role === 'implementor' && (
                  <option value="implementor">Implementor</option>
                )}
              </select>
              {errors.role && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.role}</p>
              )}
              {!canEditRole && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Role cannot be modified</p>
              )}
            </div>

            {/* Department */}
            <div>
              <label htmlFor="edit-department" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Department
              </label>
              <Input
                id="edit-department"
                type="text"
                value={formData.department}
                onChange={handleInputChange('department')}
                placeholder="Enter department (optional)..."
                disabled={updateUserMutation.loading}
              />
            </div>

            {/* Status */}
            <div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="edit-deactivated"
                  checked={formData.deactivated}
                  onChange={handleInputChange('deactivated')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={updateUserMutation.loading}
                />
                <label htmlFor="edit-deactivated" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                  User is deactivated
                </label>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={updateUserMutation.loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={updateUserMutation.loading}
              disabled={updateUserMutation.loading}
            >
              Update User
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== PASSWORD RESET MODAL =====
const PasswordResetModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  user?: User;
}> = ({ isOpen, onClose, user }) => {
  const [formData, setFormData] = useState<PasswordResetData>({
    newPassword: '',
    confirmPassword: '',
    generatePassword: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generatedPassword, setGeneratedPassword] = useState<string>('');
  const { setGlobalError, clearGlobalError } = useAppStore();

  const resetPasswordMutation = useResetUserPassword({
    onSuccess: (data: any) => {
      // If password was generated, show it to the user
      if (data?.temporaryPassword) {
        setGeneratedPassword(data.temporaryPassword);
      }
      clearGlobalError();
      // Don't close modal immediately if showing generated password
      if (!data?.temporaryPassword) {
        onClose();
      }
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to reset password');
    }
  });

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.generatePassword) {
      if (!formData.newPassword) {
        newErrors.newPassword = 'Password is required';
      } else if (formData.newPassword.length < 8) {
        newErrors.newPassword = 'Password must be at least 8 characters';
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/.test(formData.newPassword)) {
        newErrors.newPassword = 'Password must contain uppercase, lowercase, number, and special character';
      }
      
      if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?._id && !user?.id) return;
    
    if (validateForm()) {
      const resetData: any = {
        userId: user._id || user.id!,
        generatePassword: formData.generatePassword
      };

      if (!formData.generatePassword && formData.newPassword) {
        resetData.newPassword = formData.newPassword;
      }

      resetPasswordMutation.mutate(resetData);
    }
  }, [formData, validateForm, resetPasswordMutation, user]);

  const handleInputChange = useCallback((field: keyof PasswordResetData) => 
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setFormData(prev => ({ ...prev, [field]: value }));
      
      // Clear error when user starts typing
      if (errors[field as string]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field as string];
          return newErrors;
        });
      }
    }, [errors]);

  const handleClose = useCallback(() => {
    setFormData({
      newPassword: '',
      confirmPassword: '',
      generatePassword: false
    });
    setErrors({});
    setGeneratedPassword('');
    onClose();
  }, [onClose]);

  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // Could add a toast notification here
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  }, []);

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Reset Password: {user.username}
          </h3>

          {generatedPassword ? (
            /* Show generated password */
            <div className="space-y-4">
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <h4 className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">
                  Password Reset Successful
                </h4>
                <p className="text-sm text-green-700 dark:text-green-400 mb-3">
                  New temporary password for {user.username}:
                </p>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-sm font-mono">
                    {generatedPassword}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(generatedPassword)}
                  >
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                  Please share this password securely with the user. They will be required to change it on first login.
                </p>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleClose}>
                  Done
                </Button>
              </div>
            </div>
          ) : (
            /* Password reset form */
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Generate Password Option */}
                <div>
                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id="reset-generatePassword"
                      checked={formData.generatePassword}
                      onChange={handleInputChange('generatePassword')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      disabled={resetPasswordMutation.loading}
                    />
                    <label htmlFor="reset-generatePassword" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Generate secure password automatically (recommended)
                    </label>
                  </div>

                  {!formData.generatePassword && (
                    <div className="space-y-3">
                      <div>
                        <label htmlFor="reset-newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          New Password *
                        </label>
                        <Input
                          id="reset-newPassword"
                          type="password"
                          value={formData.newPassword}
                          onChange={handleInputChange('newPassword')}
                          placeholder="Enter new password..."
                          className={cn(errors.newPassword && 'border-red-500 focus:border-red-500')}
                          disabled={resetPasswordMutation.loading}
                        />
                        {errors.newPassword && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.newPassword}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="reset-confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Confirm Password *
                        </label>
                        <Input
                          id="reset-confirmPassword"
                          type="password"
                          value={formData.confirmPassword}
                          onChange={handleInputChange('confirmPassword')}
                          placeholder="Confirm new password..."
                          className={cn(errors.confirmPassword && 'border-red-500 focus:border-red-500')}
                          disabled={resetPasswordMutation.loading}
                        />
                        {errors.confirmPassword && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.confirmPassword}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Warning */}
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    <strong>Warning:</strong> This will immediately change the user's password. They will need to use the new password to log in.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={resetPasswordMutation.loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={resetPasswordMutation.loading}
                  disabled={resetPasswordMutation.loading}
                >
                  Reset Password
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== CREATE USER MODAL =====
const CreateUserModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  organizations: SimpleOrganization[];
  currentUser: any;
  selectedOrganization?: string;
  onSuccess?: () => void;
}> = ({ isOpen, onClose, organizations, currentUser, selectedOrganization, onSuccess }) => {
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    email: '',
    phoneNumber: '',
    role: 'contributor',
    department: '',
    organizationId: selectedOrganization || '',
    generatePassword: false,
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { setGlobalError, clearGlobalError } = useAppStore();

  const createUserMutation = useCreateUser({
    onSuccess: () => {
      onClose();
      clearGlobalError();
      onSuccess?.(); // Refresh the users list
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to create user');
    }
  });

  // Update organizationId when selectedOrganization changes
  useEffect(() => {
    if (selectedOrganization && formData.organizationId !== selectedOrganization) {
      setFormData(prev => ({ ...prev, organizationId: selectedOrganization }));
    }
  }, [selectedOrganization, formData.organizationId]);

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (formData.username.length > 30) {
      newErrors.username = 'Username must be less than 30 characters';
    } else if (!/^[a-zA-Z0-9._-]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, periods, underscores, and hyphens';
    }
    
    // Email is optional, but validate format if provided
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone number is optional, but validate format if provided
    if (formData.phoneNumber && formData.phoneNumber.trim()) {
      const digitsOnly = formData.phoneNumber.replace(/\D/g, '');
      if (!/^[\d\s\-\+\(\)]+$/.test(formData.phoneNumber)) {
        newErrors.phoneNumber = 'Phone number can only contain digits, spaces, and +()-';
      } else if (digitsOnly.length < 10) {
        newErrors.phoneNumber = 'Phone number must be at least 10 digits';
      }
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }
    
    if (!formData.organizationId) {
      newErrors.organizationId = 'Organization is required';
    }
    
    // Role validation based on current user
    if (currentUser?.role === 'admin' && formData.role === 'implementor') {
      newErrors.role = 'Admins cannot create implementor users';
    }
    
    // Password validation if not auto-generating
    if (!formData.generatePassword) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/.test(formData.password)) {
        newErrors.password = 'Password must contain uppercase, lowercase, number, and special character';
      }
      
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, currentUser?.role]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      const createData: any = {
        username: formData.username.trim(),
        email: formData.email.trim() || undefined,
        phoneNumber: formData.phoneNumber?.trim() || undefined,
        role: formData.role,
        organizationId: formData.organizationId
      };

      // Add optional fields
      if (formData.department?.trim()) {
        createData.department = formData.department.trim();
      }
      
      if (formData.generatePassword) {
        createData.generatePassword = true;
      } else if (formData.password) {
        createData.password = formData.password;
      }
      
      createUserMutation.mutate(createData as UserCreateRequest);
    }
  }, [formData, validateForm, createUserMutation]);

  const handleInputChange = useCallback((field: keyof UserFormData) => 
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
      setFormData(prev => ({ ...prev, [field]: value }));
      
      // Clear error when user starts typing
      if (errors[field as string]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field as string];
          return newErrors;
        });
      }
    }, [errors]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        username: '',
        email: '',
        phoneNumber: '',
        role: 'contributor',
        department: '',
        organizationId: selectedOrganization || (currentUser?.role === 'admin' && currentUser?.organization
          ? (typeof currentUser.organization === 'string' ? currentUser.organization : (currentUser.organization as any)._id)
          : ''),
        generatePassword: false,
        password: '',
        confirmPassword: ''
      });
      setErrors({});
    }
  }, [isOpen, currentUser, selectedOrganization]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Create New User
          </h3>

          <div className="space-y-4">
            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Username *
              </label>
              <Input
                id="username"
                type="text"
                value={formData.username}
                onChange={handleInputChange('username')}
                placeholder="Enter username..."
                className={cn(errors.username && 'border-red-500 focus:border-red-500')}
                disabled={createUserMutation.loading}
              />
              {errors.username && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.username}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email (optional)
              </label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                placeholder="Enter email address..."
                className={cn(errors.email && 'border-red-500 focus:border-red-500')}
                disabled={createUserMutation.loading}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phone Number (optional)
              </label>
              <Input
                id="phoneNumber"
                type="tel"
                value={formData.phoneNumber || ''}
                onChange={handleInputChange('phoneNumber')}
                placeholder="Enter phone number..."
                className={cn(errors.phoneNumber && 'border-red-500 focus:border-red-500')}
                disabled={createUserMutation.loading}
              />
              {errors.phoneNumber && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.phoneNumber}</p>
              )}
            </div>

            {/* Role */}
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role *
              </label>
              <select
                id="role"
                value={formData.role}
                onChange={handleInputChange('role')}
                className={cn(
                  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  errors.role && 'border-red-500 focus:border-red-500'
                )}
                disabled={createUserMutation.loading}
              >
                <option value="contributor">Contributor</option>
                <option value="admin">Admin</option>
                {currentUser?.role === 'implementor' && (
                  <option value="implementor">Implementor</option>
                )}
              </select>
              {errors.role && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.role}</p>
              )}
            </div>

            {/* Organization (for implementors) */}
            {currentUser?.role === 'implementor' && (
              <div>
                <label htmlFor="organizationId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Organization *
                </label>
                <select
                  id="organizationId"
                  value={formData.organizationId}
                  onChange={handleInputChange('organizationId')}
                  className={cn(
                    'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                    errors.organizationId && 'border-red-500 focus:border-red-500'
                  )}
                  disabled={createUserMutation.loading}
                >
                  <option value="">Select Organization</option>
                  {organizations.map((org) => (
                    <option key={org._id} value={org._id}>
                      {org.name}
                    </option>
                  ))}
                </select>
                {errors.organizationId && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.organizationId}</p>
                )}
              </div>
            )}

            {/* Department */}
            <div>
              <label htmlFor="department" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Department
              </label>
              <Input
                id="department"
                type="text"
                value={formData.department}
                onChange={handleInputChange('department')}
                placeholder="Enter department (optional)..."
                disabled={createUserMutation.loading}
              />
            </div>

            {/* Password Options */}
            <div>
              <div className="flex items-center mb-3">
                <input
                  type="checkbox"
                  id="generatePassword"
                  checked={formData.generatePassword}
                  onChange={handleInputChange('generatePassword')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={createUserMutation.loading}
                />
                <label htmlFor="generatePassword" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                  Generate secure password automatically
                </label>
              </div>

              {!formData.generatePassword && (
                <div className="space-y-3">
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Password *
                    </label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={handleInputChange('password')}
                      placeholder="Enter password..."
                      className={cn(errors.password && 'border-red-500 focus:border-red-500')}
                      disabled={createUserMutation.loading}
                    />
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Confirm Password *
                    </label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange('confirmPassword')}
                      placeholder="Confirm password..."
                      className={cn(errors.confirmPassword && 'border-red-500 focus:border-red-500')}
                      disabled={createUserMutation.loading}
                    />
                    {errors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.confirmPassword}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={createUserMutation.loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={createUserMutation.loading}
              disabled={createUserMutation.loading}
            >
              Create User
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== USER ROW COMPONENT =====
const UserRow: React.FC<{
  user: User;
  currentUser: NormalizedUser | null;
  onEdit: (user: User) => void;
  onDelete: (id: string) => void;
  onToggleStatus: (id: string, shouldActivate: boolean) => void;
  onResetPassword: (user: User) => void;
}> = memo(({ user, currentUser, onEdit, onDelete, onToggleStatus, onResetPassword }) => {
  const navigate = useNavigate();

  const permissions = useMemo(() => {
    if (!currentUser) return { canEdit: false, canDelete: false, canResetPassword: false };

    // Self-actions (users can always edit their own profile, except deletion)
    const isSelf = user._id === currentUser._id || user.id === currentUser.id;

    // User must be deactivated to be deleted
    const isDeactivated = user.deactivated === true;

    if (currentUser.role === 'implementor') {
      return {
        canEdit: true,
        canDelete: !isSelf && isDeactivated, // Can't delete themselves, and user must be deactivated
        canResetPassword: true
      };
    } else if (currentUser.role === 'admin') {
      return {
        canEdit: user.role !== 'implementor', // Admins can't edit implementors
        canDelete: user.role !== 'implementor' && !isSelf && isDeactivated, // Can't delete implementors or themselves, and user must be deactivated
        canResetPassword: user.role !== 'implementor' // Can't reset implementor passwords
      };
    } else {
      return {
        canEdit: isSelf, // Contributors can only edit themselves
        canDelete: false, // Contributors can't delete anyone
        canResetPassword: false // Contributors can't reset passwords
      };
    }
  }, [user, currentUser]);

  const handleEdit = useCallback(() => onEdit(user), [onEdit, user]);
  const handleDelete = useCallback(() => onDelete(user._id || user.id!), [onDelete, user]);
  const handleToggleStatus = useCallback(() => {
    onToggleStatus(user._id || user.id!, user.deactivated || false);
  }, [onToggleStatus, user]);
  const handleResetPassword = useCallback(() => onResetPassword(user), [onResetPassword, user]);

  const organizationName = useMemo(() => {
    if (typeof user.organization === 'string') return 'Unknown';
    return user.organization?.name || 'Unknown';
  }, [user.organization]);

  const organizationId = useMemo(() => {
    if (typeof user.organization === 'string') return user.organization;
    return user.organization?._id || user.organization?.id || null;
  }, [user.organization]);

  const handleOrganizationClick = useCallback(() => {
    if (organizationId && currentUser?.role === 'implementor') {
      // Navigate to organizations page with this org selected
      navigate(`/organizations?org=${organizationId}`);
    }
  }, [organizationId, currentUser?.role, navigate]);

  // Check if this is the current user
  const isCurrentUser = currentUser && (
    (user._id && user._id === currentUser._id) ||
    (user.id && user.id === (currentUser as any).id)
  );

  // Count email accounts and groups
  const emailAccountsCount = user.emailAccounts?.length || 0;
  const groupsCount = Array.isArray(user.groups) ? user.groups.length : 0;

  return (
    <tr className={cn(
      "border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50",
      isCurrentUser && "bg-blue-50 dark:bg-blue-900/10"
    )}>
      <td className="px-4 py-3">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {user.username?.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
          <div className="ml-4">
            <div className="flex items-center space-x-2">
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {user.username}
              </div>
              {isCurrentUser && (
                <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                  You
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {user.email}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
        <span className={cn(
          'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
          user.role === 'implementor' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300' :
          user.role === 'admin' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300' :
          'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
        )}>
          {user.role}
        </span>
      </td>
      <td className="hidden lg:table-cell px-4 py-3 text-sm text-gray-900 dark:text-white">
        {organizationId && currentUser?.role === 'implementor' ? (
          <button
            onClick={handleOrganizationClick}
            className="text-blue-600 dark:text-blue-400 hover:underline hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
          >
            {organizationName}
          </button>
        ) : (
          organizationName
        )}
      </td>
      <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-900 dark:text-white">
        {user.department || '-'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
        <div className="flex items-center space-x-1">
          <span className="font-medium">{emailAccountsCount}</span>
          {emailAccountsCount > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {emailAccountsCount === 1 ? 'account' : 'accounts'}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
        <div className="flex items-center space-x-1">
          <span className="font-medium">{groupsCount}</span>
          {groupsCount > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {groupsCount === 1 ? 'group' : 'groups'}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
        <span className={cn(
          'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
          user.deactivated
            ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
            : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
        )}>
          {user.deactivated ? 'Inactive' : 'Active'}
        </span>
      </td>
      <td className="hidden xl:table-cell px-4 py-3 text-sm text-gray-900 dark:text-white">
        {user.lastLogin || user.lastLoginAt ? formatDate(user.lastLogin || user.lastLoginAt) : (
          <span className="text-gray-400 dark:text-gray-500">Never</span>
        )}
      </td>
      <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-900 dark:text-white">
        {user.createdAt ? formatDate(user.createdAt) : '-'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
        <div className="flex items-center space-x-2">
          {permissions.canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleEdit}
              className="text-xs"
            >
              Edit
            </Button>
          )}
          
          {permissions.canResetPassword && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetPassword}
              className="text-xs text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
            >
              Reset Password
            </Button>
          )}
          
          {permissions.canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleStatus}
              className={cn(
                'text-xs',
                user.deactivated
                  ? 'text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300'
                  : 'text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300'
              )}
            >
              {user.deactivated ? 'Activate' : 'Deactivate'}
            </Button>
          )}
          
          {permissions.canDelete && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              Delete
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
});

UserRow.displayName = 'UserRow';

// ===== MAIN USERS PAGE COMPONENT =====
export const UsersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  // Initialize selectedOrganization from URL query param
  const [selectedOrganization, setSelectedOrganization] = useState(
    searchParams.get('org') || ''
  );
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | undefined>();
  const [passwordResetUser, setPasswordResetUser] = useState<User | undefined>();

  const { getSafeUser } = useAuthStore();
  const { setGlobalError, clearGlobalError, globalError } = useAppStore();
  const { confirm } = useConfirmation();
  const currentUser = getSafeUser();

  // Debounced search term
  const debouncedSearchTerm = useDebounced(searchTerm, 300);

  // Load organizations for implementors
  const { 
    data: organizationsData, 
    loading: orgsLoading,
    error: orgsError 
  } = useOrganizations(currentUser?.role === 'implementor' ? {} : undefined, {
    immediate: currentUser?.role === 'implementor'
  });

  // Process organizations data
  const organizations = useMemo((): SimpleOrganization[] => {
    if (currentUser?.role !== 'implementor' || !organizationsData) {
      // For non-implementors, use their own organization
      if (currentUser?.organization) {
        const org = typeof currentUser.organization === 'string' 
          ? { _id: currentUser.organization, name: 'Your Organization' }
          : { _id: (currentUser.organization as any)._id || (currentUser.organization as any).id, name: (currentUser.organization as any).name || 'Your Organization' };
        return [org];
      }
      return [];
    }
    
    const response = organizationsData as any;
    const orgs = response.data || response.organizations || [];
    return orgs.map((org: any) => ({
      _id: org._id || org.id,
      name: org.name
    }));
  }, [organizationsData, currentUser]);

  // Query params - now requires organization selection for implementors
  const queryParams = useMemo((): UsersQueryParams | undefined => {

    // For implementors, require organization selection
    if (currentUser?.role === 'implementor') {
      if (!selectedOrganization) {
        return undefined; // Don't query until org is selected
      }
      
      const params: UsersQueryParams = {
        organizationId: selectedOrganization
      };
      
      if (debouncedSearchTerm?.trim()) params.search = debouncedSearchTerm.trim();
      if (selectedRole && selectedRole !== 'all') params.role = selectedRole as UserRole;
      if (selectedStatus && selectedStatus !== 'all') params.status = selectedStatus as 'active' | 'deactivated';
      
      return params;
    } else {
      // For non-implementors, automatically use their organization
      const params: UsersQueryParams = {};
      
      if (currentUser?.organization) {
        const orgId = typeof currentUser.organization === 'string' 
          ? currentUser.organization 
          : (currentUser.organization as any)._id;
        if (orgId) {
          params.organizationId = orgId;
        }
      }
      
      if (debouncedSearchTerm?.trim()) params.search = debouncedSearchTerm.trim();
      if (selectedRole && selectedRole !== 'all') params.role = selectedRole as UserRole;
      if (selectedStatus && selectedStatus !== 'all') params.status = selectedStatus as 'active' | 'deactivated';
      
      return params;
    }
  }, [debouncedSearchTerm, selectedRole, selectedStatus, selectedOrganization, currentUser]);

  // Load users with proper parameter handling
  const { 
    data: usersData, 
    loading, 
    error, 
    refetch: refetchUsers
  } = useUsers(queryParams, {
    immediate: queryParams !== undefined
  });


  // Error handling
  useEffect(() => {
    if (error) {
      setGlobalError(`Users loading error: ${error}`);
    } else if (orgsError) {
      setGlobalError(`Organizations loading error: ${orgsError}`);
    } else {
      clearGlobalError();
    }
  }, [error, orgsError, setGlobalError, clearGlobalError]);

  // Mutation hooks
  const deleteUserMutation = useDeleteUser({
    onSuccess: () => {
      refetchUsers();
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(`Failed to delete user: ${error.message || error}`);
    }
  });

  const activateUserMutation = useActivateUser({
    onSuccess: () => {
      refetchUsers();
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(`Failed to activate user: ${error.message || error}`);
    }
  });

  const deactivateUserMutation = useDeactivateUser({
    onSuccess: () => {
      refetchUsers();
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(`Failed to deactivate user: ${error.message || error}`);
    }
  });

  // Extract data from responses
  const users = usersData?.data || [];

  // Event handlers
  const handleOrganizationChange = useCallback((orgId: string) => {
    setSelectedOrganization(orgId);
    // Update URL params
    if (orgId) {
      setSearchParams({ org: orgId });
    } else {
      setSearchParams({});
    }
  }, [setSearchParams]);

  const handleCreateUser = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleEditUser = useCallback((user: User) => {
    setEditingUser(user);
    setShowEditModal(true);
  }, []);

  const handleDeleteUser = useCallback(async (userId: string) => {
    const confirmed = await confirm({
      title: 'Delete User',
      message: 'Are you sure you want to delete this user? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      deleteUserMutation.mutate(userId);
    }
  }, [deleteUserMutation, confirm]);

  const handleToggleUserStatus = useCallback(async (userId: string, shouldActivate: boolean) => {
    if (shouldActivate) {
      activateUserMutation.mutate(userId);
    } else {
      const confirmed = await confirm({
        title: 'Deactivate User',
        message: 'Are you sure you want to deactivate this user?',
        confirmText: 'Deactivate',
        variant: 'warning',
      });
      if (confirmed) {
        deactivateUserMutation.mutate(userId);
      }
    }
  }, [activateUserMutation, deactivateUserMutation, confirm]);

  const handleResetPassword = useCallback((user: User) => {
    setPasswordResetUser(user);
    setShowPasswordModal(true);
  }, []);

  const handleRefresh = useCallback(() => {
    refetchUsers();
  }, [refetchUsers]);

  // Permissions
  const permissions = useMemo(() => {
    return {
      canCreate: currentUser?.role === 'admin' || currentUser?.role === 'implementor',
      canView: true
    };
  }, [currentUser?.role]);

  // Get current organization name for display
  const currentOrgName = useMemo(() => {
    if (currentUser?.role === 'implementor' && selectedOrganization) {
      const org = organizations.find(o => o._id === selectedOrganization);
      return org?.name || 'Selected Organization';
    } else if (currentUser?.organization) {
      if (typeof currentUser.organization === 'string') return 'Your Organization';
      return (currentUser.organization as any).name || 'Your Organization';
    }
    return 'Users';
  }, [currentUser, selectedOrganization, organizations]);

  // Actions for page header
  const actions = useMemo(() => (
    <div className="flex items-center space-x-3">
      {/* Search */}
      <div className="flex items-center space-x-3">
        <Input
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-64"
        />

        {/* Filters */}
        <select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="">All Roles</option>
          <option value="implementor">Implementor</option>
          <option value="admin">Admin</option>
          <option value="contributor">Contributor</option>
        </select>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="deactivated">Inactive</option>
        </select>

        {/* Organization selector for implementors */}
        {currentUser?.role === 'implementor' && (
          <select
            value={selectedOrganization}
            onChange={(e) => handleOrganizationChange(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value="">Select Organization</option>
            {organizations.map((org) => (
              <option key={org._id} value={org._id}>
                {org.name}
              </option>
            ))}
          </select>
        )}
      </div>
      
      <Button
        onClick={handleRefresh}
        variant="secondary"
        disabled={loading}
      >
        {loading ? <LoadingSpinner size="sm" /> : 'Refresh'}
      </Button>
      
      {permissions.canCreate && (
        <Button
          onClick={handleCreateUser}
          disabled={loading || (currentUser?.role === 'implementor' && !selectedOrganization)}
        >
          Create User
        </Button>
      )}
    </div>
  ), [
    searchTerm,
    selectedRole,
    selectedStatus,
    selectedOrganization,
    currentUser?.role,
    organizations,
    loading,
    permissions.canCreate,
    handleRefresh,
    handleCreateUser,
    handleOrganizationChange
  ]);

  // Show organization selection for implementors without selection
  if (currentUser?.role === 'implementor' && organizations.length > 0 && !selectedOrganization) {
    return (
      <PageLayout 
        title="Users" 
        subtitle="Select an organization to view and manage users"
        actions={actions}
      >
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
          <div className="text-4xl mb-4">👥</div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Select Organization
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            Please select an organization to view and manage users.
          </p>
          <div className="max-w-sm mx-auto">
            <select
              value={selectedOrganization}
              onChange={(e) => handleOrganizationChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="">Choose Organization</option>
              {organizations.map((org) => (
                <option key={org._id} value={org._id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title="Users" 
      subtitle={`Manage ${currentOrgName} users • ${users.length} total`}
      actions={actions}
      loading={loading && users.length === 0}
    >
      {/* Global Error Display */}
      {globalError && (
        <div className="mb-6">
          <ErrorAlert 
            message={globalError} 
            onClose={clearGlobalError}
          />
        </div>
      )}

      {/* Main Content */}
      {loading && users.length === 0 ? (
        <div className="flex items-center justify-center h-96">
          <LoadingSpinner size="lg" />
        </div>
      ) : users.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
          <div className="text-4xl mb-4">👤</div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Users Found
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {debouncedSearchTerm 
              ? `No users match your search criteria "${debouncedSearchTerm}"`
              : "No users have been created yet"}
          </p>
          {permissions.canCreate && (
            <Button onClick={handleCreateUser}>
              Create First User
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Email Accounts
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Groups
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="hidden xl:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((user) => (
                  <UserRow
                    key={user._id || user.id}
                    user={user}
                    currentUser={currentUser}
                    onEdit={handleEditUser}
                    onDelete={handleDeleteUser}
                    onToggleStatus={handleToggleUserStatus}
                    onResetPassword={handleResetPassword}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        organizations={organizations}
        currentUser={currentUser}
        selectedOrganization={selectedOrganization}
        onSuccess={refetchUsers}
      />

      <EditUserModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingUser(undefined);
        }}
        user={editingUser}
        organizations={organizations}
        currentUser={currentUser}
        onSuccess={refetchUsers}
      />

      <PasswordResetModal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setPasswordResetUser(undefined);
        }}
        user={passwordResetUser}
      />
    </PageLayout>
  );
};